import { Injectable }       from '@angular/core';
import {
	Ers,
	ErsDeviceTypeEnum,
	ErsExternal,
	ErsExternalStatusEnum,
	ErsStatusEnum
}                           from '@app/modules/ers/models/ers.model';
import { SanitizerService } from '@http/sanitizer.service';
import { ModelTypeEnum }    from '@models/transition.model';
import { ErsStatusEnumDTO } from '@openApiGenerated/admin-api';
import {
	ErsBodyDTO,
	ErsDeviceTypeEnumDTO,
	ExtErsBodyDTO,
	ExtErsStatusEnumDTO
}                           from '@openApiGenerated/management-api';
import { LoggerService }    from '@shared/services/logger.service';

@Injectable( {
	providedIn: 'root'
} )
export class ErsMapperService {

	constructor(
		private _logger: LoggerService
	) {}

	get ers() {
		return {
			deviceTypeDtoToTmo: this.mapErsDeviceTypeDtoToTmo.bind( this ) as typeof this.mapErsDeviceTypeDtoToTmo,
			deviceTypeTmoToDto: this.mapErsDeviceTypeTmoToDto.bind( this ) as typeof this.mapErsDeviceTypeTmoToDto,
			ersToErsBodyDTO: this.mapErsToErsBodyDTO.bind( this ) as typeof this.mapErsToErsBodyDTO,
			ersExternalToExtErsBodyDTO: this.mapErsExternalToExtErsBodyDTO.bind( this ) as typeof this.mapErsExternalToExtErsBodyDTO,
			statusDtoToTmo: this.mapStatusDtoToTmo.bind( this ) as typeof this.mapStatusDtoToTmo,
			statusTmoToDto: this.mapStatusTmoToDto.bind( this ) as typeof this.mapStatusTmoToDto,
			statusListTmoToDto: this.mapStatusListTmoToDto.bind( this ) as typeof this.mapStatusListTmoToDto,
			statusExternalDtoToTmo: this.mapStatusExternalDtoToTmo.bind( this ) as typeof this.mapStatusExternalDtoToTmo,
			statusExternalTmoToDto: this.mapStatusExternalTmoToDto.bind( this ) as typeof this.mapStatusExternalTmoToDto,
			statusListExternalTmoToDto: this.mapStatusListExternalTmoToDto.bind( this ) as typeof this.mapStatusListExternalTmoToDto
		};
	}

	private mapErsDeviceTypeDtoToTmo( ersDeviceType: ErsDeviceTypeEnumDTO ): ErsDeviceTypeEnum {
		switch ( ersDeviceType ) {
			case ErsDeviceTypeEnumDTO.APP_POS:
				return ErsDeviceTypeEnum.APP_POS;

			case ErsDeviceTypeEnumDTO.CASH_REGISTER:
				return ErsDeviceTypeEnum.CASH_REGISTER;

			case ErsDeviceTypeEnumDTO.DISTANCE_COUNTER:
				return ErsDeviceTypeEnum.DISTANCE_COUNTER;

			case ErsDeviceTypeEnumDTO.PC_POS:
				return ErsDeviceTypeEnum.PC_POS;

			case ErsDeviceTypeEnumDTO.TAXIMETER:
				return ErsDeviceTypeEnum.TAXIMETER;

			default:
				if ( ersDeviceType ) {
					this._logger.warn( `mapErsDeviceTypeDtoToTmo has no case for DTO ${ ersDeviceType }` );
				}
				return null;
		}
	}

	private mapErsDeviceTypeTmoToDto( ersDeviceType: ErsDeviceTypeEnum ): ErsDeviceTypeEnumDTO {
		switch ( ersDeviceType ) {
			case ErsDeviceTypeEnum.APP_POS:
				return ErsDeviceTypeEnumDTO.APP_POS;

			case ErsDeviceTypeEnum.CASH_REGISTER:
				return ErsDeviceTypeEnumDTO.CASH_REGISTER;

			case ErsDeviceTypeEnum.DISTANCE_COUNTER:
				return ErsDeviceTypeEnumDTO.DISTANCE_COUNTER;

			case ErsDeviceTypeEnum.PC_POS:
				return ErsDeviceTypeEnumDTO.PC_POS;

			case ErsDeviceTypeEnum.TAXIMETER:
				return ErsDeviceTypeEnumDTO.TAXIMETER;

			default:
				if ( ersDeviceType ) {
					this._logger.warn( `mapErsDeviceTypeTmoToDto has no case for TMO ${ ersDeviceType }` );
				}
				return null;
		}
	}

	private mapErsToErsBodyDTO( ers: Ers, sanitize: SanitizerService ): ErsBodyDTO {
		return {
			acquisitionDate: sanitize.date<string>( ers.acquisitionDate, ModelTypeEnum.DTO ),
			brand: ers.brand,
			commissioningDate: sanitize.date<string>( ers.commissioningDate, ModelTypeEnum.DTO ),
			ersIdentifier: ers.ersIdentifier,
			ersSerialNumber: ers.ersSerialNumber,
			deactivationReason: ers.deactivationReason,
			decommissionDate: sanitize.date<string>( ers.decommissionDate, ModelTypeEnum.DTO ),
			detailedDescription: ers.detailedDescription,
			deviceType: this.ers.deviceTypeTmoToDto( ers.deviceType ),
			model: ers.model,
			software: ers.software,
			taxOfficeRemark: ers.taxOfficeRemark,
			tssEntityId: ers.tssEntityId,
			type: ers.type,
			version: ers.version
		};
	}

	private mapErsExternalToExtErsBodyDTO( ers: ErsExternal, sanitize: SanitizerService ): ExtErsBodyDTO {
		return {
			acquisitionDate: sanitize.date<string>( ers.acquisitionDate, ModelTypeEnum.DTO ),
			commissioningDate: sanitize.date<string>( ers.commissioningDate, ModelTypeEnum.DTO ),
			brand: ers.brand,
			ersIdentifier: ers.ersIdentifier,
			ersSerialNumber: ers.ersSerialNumber,
			extTssEntityId: ers.tssEntityId,
			deactivationDate: sanitize.date<string>( ers.deactivationDate, ModelTypeEnum.DTO ),
			deactivationReason: ers.deactivationReason,
			detailedDescription: ers.detailedDescription,
			deviceType: this.ers.deviceTypeTmoToDto( ers.deviceType ),
			model: ers.model,
			software: ers.software,
			status: this.ers.statusExternalTmoToDto( ers.status ),
			taxOfficeRemark: ers.taxOfficeRemark,
			tssConnectionDate: sanitize.date<string>( ers.tssConnectionDate, ModelTypeEnum.DTO ),
			version: ers.version
		};
	}

	private mapStatusDtoToTmo( status: ErsStatusEnumDTO ): ErsStatusEnum {
		switch ( status ) {
			case ErsStatusEnumDTO.ACTIVE:
				return ErsStatusEnum.ACTIVE;

			case ErsStatusEnumDTO.DELETED:
				return ErsStatusEnum.DELETED;

			case ErsStatusEnumDTO.DISABLED:
				return ErsStatusEnum.DISABLED;

			case ErsStatusEnumDTO.INACTIVE:
				return ErsStatusEnum.INACTIVE;

			case ErsStatusEnumDTO.SUSPENDED:
				return ErsStatusEnum.SUSPENDED;

			default:
				return null;
		}
	}

	private mapStatusTmoToDto( status: ErsStatusEnum ): ErsStatusEnumDTO {
		switch ( status ) {
			case ErsStatusEnum.ACTIVE:
				return ErsStatusEnumDTO.ACTIVE;

			case ErsStatusEnum.DELETED:
				return ErsStatusEnumDTO.DELETED;

			case ErsStatusEnum.DISABLED:
				return ErsStatusEnumDTO.DISABLED;

			case ErsStatusEnum.INACTIVE:
				return ErsStatusEnumDTO.INACTIVE;

			case ErsStatusEnum.SUSPENDED:
				return ErsStatusEnumDTO.SUSPENDED;

			default:
				return null;
		}
	}

	private mapStatusListTmoToDto( statusList: ErsStatusEnum[] ): ErsStatusEnumDTO[] {
		const dtoStatusList: ErsStatusEnumDTO[] = statusList?.map( this.mapStatusTmoToDto );
		return dtoStatusList;
	}

	private mapStatusExternalDtoToTmo( status: ExtErsStatusEnumDTO ): ErsExternalStatusEnum {
		switch ( status ) {
			case ExtErsStatusEnumDTO.ACTIVE:
				return ErsExternalStatusEnum.ACTIVE;

			case ExtErsStatusEnumDTO.DELETED:
				return ErsExternalStatusEnum.DELETED;

			case ExtErsStatusEnumDTO.DISABLED:
				return ErsExternalStatusEnum.DISABLED;

			case ExtErsStatusEnumDTO.INACTIVE:
				return ErsExternalStatusEnum.INACTIVE;

			default:
				return null;
		}
	}

	private mapStatusExternalTmoToDto( status: ErsExternalStatusEnum ): ExtErsStatusEnumDTO {
		switch ( status ) {
			case ErsExternalStatusEnum.ACTIVE:
				return ExtErsStatusEnumDTO.ACTIVE;

			case ErsExternalStatusEnum.DELETED:
				return ExtErsStatusEnumDTO.DELETED;

			case ErsExternalStatusEnum.DISABLED:
				return ExtErsStatusEnumDTO.DISABLED;

			case ErsExternalStatusEnum.INACTIVE:
				return ExtErsStatusEnumDTO.INACTIVE;

			default:
				return null;
		}
	}

	private mapStatusListExternalTmoToDto( statusList: ErsExternalStatusEnum[] ): ExtErsStatusEnumDTO[] {
		const dtoStatusList: ExtErsStatusEnumDTO[] = statusList?.map( this.mapStatusExternalTmoToDto );
		return dtoStatusList;
	}
}
